/**
 * Mirror of supabase/functions/_shared/skills.ts (Deno cannot import src/).
 * skillTaxonomyParity.test.ts fails when the two drift. Never rename an
 * entry — the names are stored on profiles and quests.
 */
export const SKILL_TAXONOMY: Record<string, string[]> = {
  Development: [
    "JavaScript",
    "TypeScript",
    "Python",
    "Rust",
    "Solidity",
    "React",
    "Node.js",
    "Mobile Dev",
    "DevOps",
    "Smart Contracts",
    "Web Scraping",
    "API Development",
  ],
  Design: [
    "UI Design",
    "UX Research",
    "Graphic Design",
    "Illustration",
    "Branding",
    "Animation",
    "3D Modeling",
    "Figma",
    "Video Editing",
  ],
  Writing: [
    "Copywriting",
    "Technical Writing",
    "Blog Posts",
    "SEO Content",
    "Translation",
    "Editing",
    "Scriptwriting",
    "Academic Writing",
  ],
  Research: [
    "Market Research",
    "Data Analysis",
    "User Research",
    "Academic Research",
    "Competitive Analysis",
    "Survey Design",
  ],
  Marketing: [
    "Social Media",
    "Email Marketing",
    "Paid Ads",
    "Content Strategy",
    "Community Management",
    "Influencer Outreach",
    "PR",
  ],
  Data: [
    "Data Entry",
    "Data Cleaning",
    "Data Visualization",
    "Machine Learning",
    "SQL",
    "Spreadsheets",
    "ETL",
  ],
  Learning: [
    "Music Practice",
    "Language Learning",
    "Tutoring",
    "Course Creation",
    "Mentoring",
  ],
  Languages: [
    "English",
    "Spanish",
    "Portuguese",
    "French",
    "German",
    "Italian",
    "Mandarin",
    "Japanese",
    "Korean",
    "Arabic",
    "Hindi",
    "Guaraní",
  ],
  Home: [
    "Cooking",
    "Baking",
    "Cleaning",
    "Laundry",
    "Gardening",
    "Handyman",
    "Repairs",
    "Sewing",
  ],
  Care: [
    "Childcare",
    "Babysitting",
    "Elder Care",
    "Pet Care",
    "Dog Walking",
  ],
  Local: [
    "Driving",
    "Delivery",
    "Errands",
    "Moving Help",
  ],
  Music: [
    "Piano",
    "Guitar",
    "Singing",
    "Drums",
    "Music Teaching",
    "Dance",
  ],
  Sports: [
    "Sports Coaching",
    "Personal Training",
    "Yoga",
    "Football",
  ],
  Events: [
    "Event Help",
    "Event Planning",
    "Catering",
    "Bartending",
  ],
  Other: [
    "Customer Support",
    "Virtual Assistant",
    "Transcription",
    "Testing",
    "Photography",
    "Audio Production",
  ],
};

export const ALL_SKILLS = Object.values(SKILL_TAXONOMY).flat();

export const SKILL_CATEGORIES = Object.keys(SKILL_TAXONOMY);

const CANONICAL_BY_LOWER: ReadonlyMap<string, string> = new Map(
  ALL_SKILLS.map((s) => [s.toLowerCase(), s]),
);

/**
 * The taxonomy spelling of a skill name, matched case-insensitively and
 * ignoring surrounding/collapsed whitespace — "music teaching" → "Music
 * Teaching". Null when the name is not in the taxonomy. Mirrors the edge
 * function's helper of the same name.
 */
export function canonicalSkillName(raw: string): string | null {
  const key = raw.trim().replace(/\s+/g, " ").toLowerCase();
  return CANONICAL_BY_LOWER.get(key) ?? null;
}

/** What `ai-infer-skills` returns. */
export interface InferredSkills {
  /** Canonical taxonomy names. */
  skills: string[];
  /** Short skills the person named that have no taxonomy match, as they wrote them. */
  other: string[];
}

/**
 * Appends skills to a list the way a person adding chips expects: taxonomy
 * names in canonical spelling, everything else as written, and nothing that
 * is already there under a different case.
 */
export function mergeSkills(prev: readonly string[], added: readonly string[]): string[] {
  const out = [...prev];
  const seen = new Set(prev.map((s) => s.trim().toLowerCase()));
  for (const raw of added) {
    const trimmed = raw.trim();
    if (!trimmed) continue;
    const skill = canonicalSkillName(trimmed) ?? trimmed;
    const key = skill.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(skill);
  }
  return out;
}

/**
 * One inference result as a flat chip list: taxonomy skills first, then the
 * free-text ones, which become custom chips exactly like a typed-in skill.
 */
export function inferredSkillList(inferred: InferredSkills): string[] {
  return mergeSkills([], [...inferred.skills, ...inferred.other]);
}

/**
 * DEV-824: the i18n key for a skill chip.
 *
 * Stored skill values are NEVER translated in the database — they are filter
 * keys (getDiscoverFeed's skills filter) and art inputs (deriveQuestScene), so
 * rewriting them would break matching and produce three spellings of
 * "Photography" across three quests. The stored value stays canonical and only
 * its LABEL is localised, at render time.
 */
export function skillLabelKey(skill: string): string {
  const slug = skill
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `skill.${slug}`;
}

/** Mirrors the SQL `skill_match_key`: lower-case, separators collapsed to one hyphen. */
export function skillKey(skill: string): string {
  return skill
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Fallback label for a skill with no translation key. Most stored skills are
 * free-text slugs ("local-errands"), which read badly as-is: "Local errands".
 * Anything already mixed-case ("UI Design") is left alone.
 */
export function humanizeSkill(skill: string): string {
  const s = skill.trim();
  if (s !== s.toLowerCase()) return s;
  const spaced = s.replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Longest custom entry kept verbatim as a chip; past it, it reads as a sentence. */
export const MAX_SKILL_CHIP_CHARS = 40;
/** Most words a custom entry may have and still be a chip ("Customer Support Team Lead"). */
export const MAX_SKILL_CHIP_WORDS = 5;

/**
 * True when free text typed into a "add a skill" box is a description of what
 * someone does rather than a skill name — e.g. "Dirijo y produzco piezas
 * audiovisuales, desde el concepto creativo hasta el rodaje." Stored verbatim,
 * those become paragraph-sized chips on every candidate card and never match a
 * quest's skills, so callers send them through skill inference instead.
 */
export function isSkillDescription(text: string): boolean {
  const s = text.trim();
  if (!s) return false;
  return s.length > MAX_SKILL_CHIP_CHARS || s.split(/\s+/).length > MAX_SKILL_CHIP_WORDS;
}
